import React, { useState, useEffect } from 'react';
import authedFetch from '../../lib/authedFetch';
import { useLocation, useNavigate } from 'react-router-dom';
import { HelpCircle, User, LogOut, Settings, Bell, Shield, ShieldCheck, Building2, ArrowLeftRight, X, Eye, FlaskConical, Info, Sun, Moon, Laptop, ScanFace } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import CurrencySelector from '../CurrencySelector';
import NotificationDropdown from '../notifications/NotificationDropdown';
import GlobalStorybookPill from './GlobalStorybookPill';
import { HeaderThemeProvider } from './HeaderThemeContext';
import IndustryChip from './IndustryChip';
import IndustryNotificationBell from './IndustryNotificationBell';
import IndustryFreshnessBadge from './IndustryFreshnessBadge';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import secureStorage from '../../lib/secureStorage';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { usePageTitle } from '../../context/PageTitleContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pageTitles = {
  '/': 'Home',
  '/impact-modelling': 'Impact Modelling',
  '/configure': 'Single Measure ROI',
  '/portfolio': 'Multi-Measure Portfolio ROI',
  '/matrix': 'Full LEI-to-KPI Impact Matrix',
  '/results': 'Results & Analysis',
  '/reports': 'Reports',
  '/import': 'Model Setup',
  '/data-management': 'Track Progress',
  '/scenarios': 'Saved Scenarios',
  '/compare': 'Compare Scenarios',
  '/dampening-compare': 'Dampening What-If',
  '/comparison-analytics': 'Comparison Analytics',
  '/goals': 'Goals & Progress',
  '/prospectus': 'Custom Business Case',
  '/scheduled-reports': 'Scheduled Reports',
  '/accuracy': 'Accuracy Leaderboard',
  '/custom-measures': 'Custom Measures',
  '/lei-reference': 'LEI Measures Guide',
  '/people-engine': 'Capability Health',
  '/people-engine/diagnostic': 'Navigation Readiness Diagnostic',
  '/people-engine/goals': 'People Engine Goals',
  '/people-engine/intervention-backlog': 'Intervention Backlog',
  '/people-engine/benchmarking': 'PE Benchmarking',
  '/people-engine/reference': 'People Engine Reference',
  '/profile': 'My Account',
  '/admin': 'Org & User Management',
  '/admin/telemetry': 'Platform Telemetry',
  '/admin/data-health': 'Data Health',
  '/admin/code-health': 'Code Health',
  '/admin/email-suppressions': 'Email Suppressions',
  '/admin/collateral': 'Collateral Library',
  '/admin/reports/builder': 'AI Report Builder',
  '/admin/glossary': 'AI Glossary',
  '/solution-finder': 'Solution Finder',
  '/business-case-builder': 'Solution Business Case',
  '/score-trends': 'Navigation Signals',
  '/lei-health': 'Learning Health',
  '/lei-dashboard': 'Navigation Health',
  '/program-analytics': 'Program Navigation Portfolio',
  '/gap-analysis': 'Value Conversion Analysis',
  '/benchmarking': 'Industry Benchmarks',
  '/org-benchmarks': 'Organization Benchmarks',
  '/segments': 'Data Segmentation',
  '/component-data-import': 'Component Data Import',
  '/usage-analytics': 'Usage Analytics',
  '/research': 'Research & Evidence',
  '/maturity-health': 'Organizational Learning Maturity',
  '/maturity-reference': 'Learning Maturity Reference',
  '/correlation-analysis': 'Correlation & Divergence Analysis',
  '/roe-health': 'Transfer Health',
  '/performance-health': 'Performance Health',
  '/story-guide': 'Read the Story',
  '/roe-modeller': 'Behavior Modeller',
  '/roi-health': 'Financial Health',
  '/data-hub': 'Data Management Hub',
  '/anomaly-dashboard': 'Anomaly Dashboard',
  '/transfer-methodology': 'Transfer Factor Methodology',
  '/roe-reference': 'ROE Reference',
};

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, userRole, viewAsOrg, setViewAsOrg, clearViewAsOrg, impersonating } = useAuth();
  const { resolvedTheme, toggle: toggleTheme, preference: themePreference, setPreference: setThemePreference } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const c = (dark, light) => (isDark ? dark : light);
  const dividerCls = c('bg-[#1f2a44]', 'bg-slate-200');
  const titleCls = c('text-slate-100', 'text-slate-900');
  const iconCls = c('text-slate-300', 'text-slate-600');
  // Live page title published by the page's <PageHero>. We use it only
  // when its registered path matches the current route (so we never
  // show a stale title mid-transition); otherwise fall back to the
  // static `pageTitles` map for routes that don't render a hero.
  const { title: liveTitle, path: livePath } = usePageTitle();
  const title =
    (livePath === location.pathname && liveTitle)
      ? liveTitle
      : (pageTitles[location.pathname] || 'Seertech');

  // Browser tab title — keeps shared screenshots & tabs unmistakably on-brand.
  // Mission Control surface lives behind the global Header; Mirror has its
  // own override inside PIELayout so it wins for /mirror/* routes.
  useDocumentTitle(
    location.pathname.startsWith('/mirror')
      ? null
      : `Mission Control · ${title} · Seertech`,
  );

  const [organizations, setOrganizations] = useState([]);

  // Fetch org list — used by the super-admin org switcher AND by the
  // header brand slot (so every user can see their own org's logo or
  // name in place of the legacy Seertech mark).
  useEffect(() => {
    if (!user) return;
    const fetchOrgs = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch { /* ignore */ }
    };
    fetchOrgs();
  }, [user]);

  // Resolve the org the user is currently scoped to: impersonated
  // org takes precedence over the user's home org.
  const effectiveOrgId = viewAsOrg?.id || user?.organization_id;
  const effectiveOrg = organizations.find(o => o.id === effectiveOrgId) || null;
  const orgLogoUrl =
    effectiveOrg?.logo_url
    || (effectiveOrg?.settings && effectiveOrg.settings.logo_url)
    || null;
  const orgName =
    effectiveOrg?.name
    || viewAsOrg?.name
    || user?.profile?.organization_name
    || null;

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out');
    navigate('/login');
  };

  const handleSwitchOrg = (org) => {
    setViewAsOrg(org.id, org.name);
    toast.success(`Now viewing as ${org.name}`);
    // Force page reload to refetch data with new org context
    window.location.reload();
  };

  const handleExitPreview = () => {
    clearViewAsOrg();
    toast.success('Returned to Super Admin view');
    window.location.reload();
  };

  return (
    <HeaderThemeProvider onDark={isDark}>
      <header className={`sticky top-0 z-40 border-b theme-surface ${c('bg-[#0a0f1c] border-[#1f2a44]', 'bg-white border-slate-200')}`} data-theme-surface={resolvedTheme}>
        <div className="flex items-center justify-between h-16 px-4 md:px-6 gap-2 md:gap-4 min-w-0">
          {/* Brand slot — org logo (or name fallback) + page title */}
          <div className="flex items-center gap-4 min-w-0">
            {orgLogoUrl ? (
              <img
                src={orgLogoUrl}
                alt={orgName || 'Organization'}
                className="h-[3.24rem] w-auto max-w-[220px] object-contain flex-shrink-0"
                data-testid="header-org-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : orgName ? (
              <span
                className={`text-base font-bold uppercase tracking-wider ${titleCls} truncate max-w-[200px] flex-shrink-0`}
                title={orgName}
                data-testid="header-org-name"
              >
                {orgName}
              </span>
            ) : (
              // Fallback to the Seertech monogram for unauthenticated/edge states
              <img
                src="/seertech_monogram.png"
                alt="Seertech"
                className="h-10 w-auto flex-shrink-0"
                data-testid="header-brand-logo"
              />
            )}
            <div className={`h-8 w-px ${dividerCls} flex-shrink-0`} />
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`hidden 2xl:block text-base font-bold uppercase tracking-wider ${titleCls} whitespace-nowrap flex-shrink-0`}
                data-testid="header-mission-control"
              >
                Mission Control
              </span>
              <span className={`hidden 2xl:block ${c('text-slate-600', 'text-slate-400')} text-base font-bold flex-shrink-0`} aria-hidden="true">·</span>
              <span
                className={`block text-base font-bold uppercase tracking-wider ${titleCls} truncate`}
                title={title}
                data-testid="header-page-title"
              >
                {title}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0 min-w-0">
            {/* Industry chip — surfaces the resolved ROI model
                for the active org. Hidden when org has no industry tagged. */}
            <IndustryChip orgId={effectiveOrgId} />

            {/* "Coefficients shifted" alert — appears when the org's
                industry profile was mutated in the last 7d by someone
                other than the current user. Closes the loop for ROI
                consumers (org admins) so they know underlying baselines
                just moved. Renders nothing otherwise. */}
            <IndustryFreshnessBadge orgId={effectiveOrgId} />

            {/* Industry Library notifications — super admins get a soft
                chip when another admin edits a profile they watch or
                created. Renders nothing for non-super-admins. */}
            <IndustryNotificationBell isSuperAdmin={isSuperAdmin} />

            {/* Org Switcher — Super Admin only */}
            {isSuperAdmin && organizations.length > 1 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={viewAsOrg ? "default" : "outline"}
                    size="sm"
                    className={
                      viewAsOrg
                        ? "bg-amber-500 hover:bg-amber-600 text-white border-0 gap-2"
                        : c("gap-2 border-[#1f2a44] bg-transparent text-slate-200 hover:bg-white/5 hover:text-white", "gap-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900")
                    }
                    data-testid="org-switcher-btn"
                  >
                    <ArrowLeftRight className="h-3.5 w-3.5" />
                    {viewAsOrg ? viewAsOrg.name : 'Switch Org'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" data-testid="org-switcher-menu">
                  <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                    View as organization
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {viewAsOrg && (
                    <>
                      <DropdownMenuItem
                        onClick={handleExitPreview}
                        className="text-indigo-600 font-medium gap-2"
                        data-testid="exit-preview-menu-item"
                      >
                        <X className="h-4 w-4" />
                        Exit Preview (All Orgs)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  {organizations.map(org => (
                    <DropdownMenuItem
                      key={org.id}
                      onClick={() => handleSwitchOrg(org)}
                      className={`gap-2 ${viewAsOrg?.id === org.id ? 'bg-amber-50 text-amber-800 font-medium' : ''}`}
                      data-testid={`org-switch-${org.id}`}
                    >
                      <Building2 className="h-4 w-4 text-slate-400" />
                      <span className="flex-1">{org.name}</span>
                      {viewAsOrg?.id === org.id && (
                        <Eye className="h-3.5 w-3.5 text-amber-500" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Currency Selector */}
            <div className="hidden md:block"><CurrencySelector compact /></div>
            
            <div className={`h-6 w-px ${dividerCls}`} />

            <Button
              variant="ghost"
              size="sm"
              data-testid="header-open-mirror-btn"
              title="Open Seertech Mirror in a new tab (already signed in)"
              aria-label="Open Seertech Mirror"
              className="gap-1.5"
              onClick={() => { const w = window.open('/mirror', 'seertech-mirror'); if (w) w.focus(); else navigate('/mirror'); }}
            >
              <ScanFace className={`h-5 w-5 ${iconCls}`} />
              <span className={`hidden lg:inline text-xs font-semibold ${iconCls}`}>Mirror</span>
            </Button>

            <div className={`h-6 w-px ${dividerCls}`} />

            <Button
              variant="ghost"
              size="icon"
              data-testid="theme-toggle-btn"
              title={resolvedTheme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label="Toggle light/dark theme"
              onClick={toggleTheme}
            >
              {resolvedTheme === 'dark'
                ? <Sun className={`h-5 w-5 ${iconCls}`} />
                : <Moon className={`h-5 w-5 ${iconCls}`} />}
            </Button>

            <div className={`h-6 w-px ${dividerCls}`} />
            
            <Button
              variant="ghost"
              size="icon"
              data-testid="help-btn"
              title="Open page guide (or press ?)"
              onClick={() => window.dispatchEvent(new CustomEvent('glossary:open'))}
            >
              <HelpCircle className={`h-5 w-5 ${iconCls}`} />
            </Button>
            
            {/* Notification Dropdown */}
            <NotificationDropdown />

            {/* Storybook status pill — super admin & org admin */}
            {(isSuperAdmin || userRole === 'org_admin') && <div className="hidden lg:block"><GlobalStorybookPill /></div>}

            {/* Role Badge */}
            {userRole && userRole !== 'user' && (
              <div className={`hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${c('bg-white/5 border-[#1f2a44]', 'bg-slate-100 border-slate-200')}`} data-testid="user-role-badge">
                {isSuperAdmin ? (
                  <ShieldCheck className={`h-3.5 w-3.5 ${c('text-indigo-400', 'text-indigo-600')}`} />
                ) : (
                  <Shield className={`h-3.5 w-3.5 ${c('text-teal-400', 'text-teal-600')}`} />
                )}
                <span className={`text-xs font-medium ${isSuperAdmin ? c('text-indigo-300', 'text-indigo-700') : c('text-teal-300', 'text-teal-700')}`}>
                  {isSuperAdmin ? 'Super Admin' : userRole === 'org_admin' ? 'Org Admin' : userRole.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </span>
              </div>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  data-testid="user-menu-btn"
                >
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-teal-600 flex items-center justify-center">
                      <User className="h-4 w-4 text-white" />
                    </div>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  {impersonating ? (
                    <>
                      <p className="text-xs text-amber-600 font-medium">Now viewing as</p>
                      <p className="text-sm font-medium text-slate-900">{impersonating.name}</p>
                      <p className="text-xs text-slate-500">{impersonating.email}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                      <p className="text-xs text-slate-500">{user?.email}</p>
                    </>
                  )}
                  {!impersonating && user?.profile?.organization_name && (
                    <p className="text-xs text-teal-600 mt-0.5">{user.profile.organization_name}</p>
                  )}
                  {!impersonating && userRole && userRole !== 'user' && (
                    <p className={`text-xs mt-0.5 ${isSuperAdmin ? 'text-indigo-600' : 'text-slate-500'}`} data-testid="dropdown-role-label">
                      {isSuperAdmin ? 'Super Admin' : userRole === 'org_admin' ? 'Org Admin' : userRole.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </p>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <Settings className="mr-2 h-4 w-4" />
                  My Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings/notifications')} data-testid="notification-prefs-link">
                  <Bell className="mr-2 h-4 w-4" />
                  Notification Preferences
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5">
                  Appearance
                </DropdownMenuLabel>
                <div className="px-2 pb-1.5" data-testid="theme-picker">
                  <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-0.5">
                    {[
                      { id: 'system', label: 'System', icon: Laptop },
                      { id: 'light', label: 'Light', icon: Sun },
                      { id: 'dark', label: 'Dark', icon: Moon },
                    ].map((opt) => {
                      const OptIcon = opt.icon;
                      const active = themePreference === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setThemePreference(opt.id)}
                          className={`flex flex-col items-center gap-1 rounded-md px-1.5 py-1.5 text-[11px] font-medium transition-colors ${active ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                          data-testid={`theme-option-${opt.id}`}
                          aria-pressed={active}
                        >
                          <OptIcon className="h-4 w-4" />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-rose-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* View-As Preview Banner — only for super admins genuinely previewing
          another org; hidden for direct org-admin/demo logins and during
          impersonation (impersonation banner handles that case). */}
      {viewAsOrg && !impersonating && isSuperAdmin && (
        <div
          className={`sticky top-16 z-30 border-b shadow-sm ${c('bg-[#141a24] border-amber-500/25 text-amber-100/90', 'bg-amber-50 border-amber-200 text-amber-900')}`}
          data-testid="view-as-banner"
        >
          <div className="flex items-center justify-between px-6 py-1.5">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              <span className="text-sm font-medium">
                Previewing as <strong>{viewAsOrg.name}</strong>
              </span>
              <span className="text-xs opacity-80">
                — All data is scoped to this organization
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`ml-1 p-0.5 rounded-full transition-colors ${c('text-white/80 hover:text-white hover:bg-white/20', 'text-amber-700 hover:text-amber-900 hover:bg-amber-100')}`}
                    aria-label="What is preview mode?"
                    data-testid="view-as-banner-info"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="start"
                  className="w-80 text-xs leading-relaxed text-slate-700"
                  style={{ zIndex: 80 }}
                  data-testid="view-as-banner-info-content"
                >
                  <div className="font-semibold text-slate-900 text-sm mb-1">Preview mode</div>
                  <p className="text-slate-600">
                    You're seeing the app exactly as a member of <strong>{viewAsOrg.name}</strong> would.
                    Every read query is scoped to this org's data only — useful for support
                    work, customer demos, and verifying tenant isolation. Click <strong>Exit Preview</strong>
                    on the right to return to your own super-admin view.
                  </p>
                </PopoverContent>
              </Popover>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExitPreview}
              className={`gap-1.5 h-7 ${c('text-amber-200 hover:bg-white/10', 'text-amber-800 hover:bg-amber-100')}`}
              data-testid="exit-preview-btn"
            >
              <X className="h-3.5 w-3.5" />
              Exit Preview
            </Button>
          </div>
        </div>
      )}

      {/* Demo Tenant Banner — shown whenever the effective org is the Acme showcase tenant */}
      {(() => {
        const DEMO_ORG_ID = 'org_acme_demo';
        const DEMO_ORG_NAME = 'Acme Industries';
        const effectiveOrgId = viewAsOrg?.id || user?.organization_id;
        if (effectiveOrgId !== DEMO_ORG_ID) return null;
        return (
          <div
            className={`sticky z-30 border-b shadow-sm ${c('bg-[#10131f] border-indigo-500/25 text-indigo-200/85', 'bg-indigo-50 border-indigo-200 text-indigo-800')}`}
            style={{ top: viewAsOrg && !impersonating && isSuperAdmin ? '6.25rem' : '4rem' }}
            data-testid="demo-tenant-banner"
          >
            <div className="flex items-center justify-center gap-2.5 px-6 py-1">
              <FlaskConical className="h-3.5 w-3.5 opacity-90" />
              <span className="text-xs font-semibold tracking-wide uppercase">
                Demo Tenant
              </span>
              <span className="text-xs opacity-70">·</span>
              <span className="text-xs font-medium">{DEMO_ORG_NAME}</span>
              <span className="text-xs opacity-70">·</span>
              <span className="text-xs opacity-90">
                All data is synthetic — for demonstration only
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className={`ml-0.5 p-0.5 rounded-full transition-colors ${c('text-white/80 hover:text-white hover:bg-white/20', 'text-indigo-600 hover:text-indigo-900 hover:bg-indigo-100')}`}
                    aria-label="What is the demo tenant?"
                    data-testid="demo-tenant-banner-info"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="end"
                  className="w-80 text-xs leading-relaxed text-slate-700"
                  style={{ zIndex: 80 }}
                  data-testid="demo-tenant-banner-info-content"
                >
                  <div className="font-semibold text-slate-900 text-sm mb-1">Demo tenant — Acme Industries</div>
                  <p className="text-slate-600">
                    Acme Industries is a synthetic showcase tenant pre-seeded with realistic skill
                    gaps, business cases, and report cards so you can demo every feature end-to-end
                    without exposing real customer data. Numbers refresh on every seed run and
                    aren't tied to anyone's actual workforce.
                  </p>
                  <p className="text-slate-600 mt-2">
                    To switch to your own tenant, open the <strong>tenant switcher</strong> next to
                    the logo at the top of the page (or exit preview mode if the orange bar is
                    showing).
                  </p>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        );
      })()}
    </HeaderThemeProvider>
  );
};

export default Header;
